import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/invoices/${token}`).then(({ data }) => setInv(data)).catch((e) => setErr(e?.response?.status === 404 ? "This invoice is not available." : "Error loading invoice."));
  }, [token]);

  if (err) return <Centered>{err}</Centered>;
  if (!inv) return <Centered>Loading…</Centered>;

  const symbol = inv.company?.currency_symbol || "₹";
  const showLogo = inv.show_invoice_logo !== false && inv.company?.logo_file_id;
  const logoUrl = showLogo ? `${BACKEND_URL}/api/public-files/${inv.company.logo_file_id}?token=${token}` : null;
  const customs = (inv.visible_custom_fields || []).map((f) => ({ ...f, value: inv.booking_custom_data?.[f.name] }))
    .filter((f) => f.value !== undefined && f.value !== null && f.value !== "");

  return (
    <div className="min-h-screen bg-[#F5F4F0] py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white border border-black/10 rounded-md p-8 md:p-12" data-testid="public-invoice-page">
        <div className="flex items-start justify-between gap-6 pb-8 border-b border-black/10">
          <div>
            {logoUrl && <img src={logoUrl} alt="Company logo" className="h-12 mb-3 object-contain" />}
            <div className="font-serif text-3xl tracking-tighter">{inv.company?.name}</div>
            {inv.company?.address && <div className="text-sm text-zinc-600 whitespace-pre-line mt-1">{inv.company.address}</div>}
            <div className="text-sm text-zinc-600 mt-1">{inv.company?.phone} · {inv.company?.email}</div>
            {inv.company?.gst_number && <div className="text-xs text-zinc-500 mt-1">GST: {inv.company.gst_number}</div>}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Invoice</div>
            <div className="font-serif text-3xl tracking-tighter">{inv.invoice_number}</div>
            <div className="text-sm text-zinc-600 mt-1">Date: {(inv.invoice_date || "").slice(0, 10)}</div>
            {inv.due_date && <div className="text-sm text-zinc-600">Due: {inv.due_date}</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Bill to</div>
            <div className="mt-2 font-medium">{inv.client_name}</div>
            <div className="text-sm text-zinc-600">{inv.client_phone}</div>
            {inv.client_email && <div className="text-sm text-zinc-600">{inv.client_email}</div>}
          </div>
          {inv.booking_event_date && (
            <div className="md:text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Event</div>
              <div className="mt-2">{inv.booking_event_type_name}</div>
              <div className="text-sm text-zinc-600">{inv.booking_event_date}</div>
            </div>
          )}
        </div>

        <table className="w-full mt-8 text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.18em] text-zinc-500 border-b border-black/10">
              <th className="text-left py-2 font-medium">Description</th>
              <th className="text-right py-2 font-medium">Qty</th>
              <th className="text-right py-2 font-medium">Rate</th>
              <th className="text-right py-2 font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items?.map((li, i) => (
              <tr key={i} className="border-b border-black/5">
                <td className="py-3">{li.description}</td>
                <td className="text-right py-3">{li.quantity}</td>
                <td className="text-right py-3">{symbol}{li.rate}</td>
                <td className="text-right py-3">{symbol}{((li.quantity || 0) * (li.rate || 0)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <dl className="space-y-1 text-sm w-full md:w-72">
            <Row k="Subtotal" v={`${symbol}${inv.subtotal?.toFixed(2)}`} />
            {inv.discount_amount > 0 && <Row k="Discount" v={`-${symbol}${inv.discount_amount?.toFixed(2)}`} />}
            <Row k={`Tax (${inv.tax_rate}%)`} v={`${symbol}${inv.tax_amount?.toFixed(2)}`} />
            <div className="flex justify-between border-t border-black/10 pt-2 mt-2">
              <span className="font-serif text-xl">Total</span>
              <span className="font-serif text-xl">{symbol}{inv.total?.toFixed(2)}</span>
            </div>
          </dl>
        </div>

        {customs.length > 0 && (
          <div className="mt-8 pt-6 border-t border-black/10">
            <span className="block uppercase tracking-[0.18em] text-zinc-500 mb-3 text-xs">Event details</span>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {customs.map((c) => (
                <div key={c.name}>
                  <dt className="text-xs uppercase tracking-[0.16em] text-zinc-500">{c.name}</dt>
                  <dd className="mt-0.5">{String(c.value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {inv.show_tnc && inv.terms_text && (
          <div className="mt-8 pt-6 border-t border-black/10 text-xs text-zinc-600 whitespace-pre-line">
            <span className="block uppercase tracking-[0.18em] text-zinc-500 mb-1">Terms & conditions</span>
            {inv.terms_text}
          </div>
        )}

        <div className="mt-12 text-center text-xs text-zinc-400">
          Generated by EventFlow · {window.location.origin}
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-600">{children}</div>;
}
function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-zinc-600">{k}</span><span>{v}</span></div>;
}
